<!-- Page Header -->
<div class="page-header">
  <div class="header-content">
    <div class="header-main">
      <button class="btn btn-outline-secondary btn-sm"
              (click)="goBack()"
              style="margin-right: 10px;">
        <i class="fa-solid fa-arrow-left"></i>
      </button>
      <div class="header-icon">
        <i class="fa-solid fa-boxes-packing"></i>
      </div>
      <div class="header-text">
        <h1 class="page-title">Restock Inventory</h1>
        <p class="page-description">Add new stock to existing inventory items</p>
      </div>
    </div>
    <div class="header-actions">
      <button class="btn btn-outline-secondary"
              (click)="clearForm()"
              [disabled]="isLoading">
        <i class="fa-solid fa-arrows-rotate"></i>
        Clear Form
      </button>
    </div>
  </div>
</div>

<!-- Main Content -->
<div class="row g-4">
  <!-- Form Card -->
  <div class="col-lg-6">
    <div class="card">
      <div style="padding: 16px 20px; border-bottom: 1px solid var(--border); background: var(--bg-muted); border-radius: var(--radius-lg) var(--radius-lg) 0 0;">
        <div style="display: flex; align-items: center; gap: 10px;">
          <div style="width: 30px; height: 30px; border-radius: var(--radius-sm); background: var(--accent-subtle); color: var(--accent); display: flex; align-items: center; justify-content: center; font-size: 13px;">
            <i class="fa-solid fa-circle-plus"></i>
          </div>
          <div>
            <div style="font-size: 14px; font-weight: 600; color: var(--text-primary);">Restock Information</div>
            <div style="font-size: 12px; color: var(--text-tertiary);">Fill in the details below to restock inventory</div>
          </div>
        </div>
      </div>
      <div style="padding: 20px;">
        @if (isLoading && !isUploadingReceipt) {
          <!-- Loading State -->
          <div class="loading-state" style="padding: 40px 20px;">
            <div class="loading-content">
              <div class="loading-spinner">
                <div class="spinner-border text-primary" role="status">
                  <span class="visually-hidden">Loading...</span>
                </div>
              </div>
              <div class="loading-text">
                <div class="loading-title">Loading inventory items...</div>
              </div>
            </div>
          </div>
        } @else {
          <form [formGroup]="restockForm" (ngSubmit)="onSubmit()">
            <div class="row g-3">
              <!-- Inventory Item Select -->
              <div class="col-12">
                <div class="form-group">
                  <label for="inventory_id" class="form-label">
                    <i class="fa-solid fa-box me-1"></i>
                    Inventory Item *
                  </label>
                  <select name="inventory_id"
                          formControlName="inventory_id"
                          class="form-select"
                          id="inventory_id"
                          [class.is-invalid]="restockForm.get('inventory_id')?.invalid && restockForm.get('inventory_id')?.touched">
                    <option value="">Choose inventory item...</option>
                    @for (item of inventories; track item.id) {
                      <option [value]="item.id">{{item.name}}</option>
                    }
                  </select>
                  @if (restockForm.get('inventory_id')?.invalid && restockForm.get('inventory_id')?.touched) {
                    <div class="invalid-feedback d-block">
                      <i class="fa-solid fa-circle-exclamation me-1"></i>
                      Please select an inventory item
                    </div>
                  }
                </div>
              </div>

              <!-- Expiry Date & Quantity -->
              <div class="col-md-6">
                <div class="form-group">
                  <label for="expiry_date" class="form-label">
                    <i class="fa-solid fa-calendar-days me-1"></i>
                    Expiry Date *
                  </label>
                  <input type="date"
                         name="expiry_date"
                         formControlName="expiry_date"
                         class="form-control"
                         id="expiry_date"
                         [class.is-invalid]="restockForm.get('expiry_date')?.invalid && restockForm.get('expiry_date')?.touched">
                  @if (restockForm.get('expiry_date')?.invalid && restockForm.get('expiry_date')?.touched) {
                    <div class="invalid-feedback d-block">
                      <i class="fa-solid fa-circle-exclamation me-1"></i>
                      Expiry date is required
                    </div>
                  }
                </div>
              </div>

              <div class="col-md-6">
                <div class="form-group">
                  <label for="quantity" class="form-label">
                    <i class="fa-solid fa-boxes-stacked me-1"></i>
                    Quantity *
                  </label>
                  <input type="number"
                         name="quantity"
                         min="1"
                         formControlName="quantity"
                         class="form-control"
                         id="quantity"
                         placeholder="0"
                         [class.is-invalid]="restockForm.get('quantity')?.invalid && restockForm.get('quantity')?.touched">
                  @if (restockForm.get('quantity')?.invalid && restockForm.get('quantity')?.touched) {
                    <div class="invalid-feedback d-block">
                      <i class="fa-solid fa-circle-exclamation me-1"></i>
                      Quantity must be at least 1
                    </div>
                  }
                </div>
              </div>

              <!-- Receipt Number -->
              <div class="col-12">
                <div class="form-group">
                  <label for="receipt_number" class="form-label">
                    <i class="fa-solid fa-receipt me-1"></i>
                    Receipt Number *
                  </label>
                  <input type="text"
                         name="receipt_number"
                         formControlName="receipt_number"
                         class="form-control"
                         id="receipt_number"
                         placeholder="Enter receipt number"
                         [class.is-invalid]="restockForm.get('receipt_number')?.invalid && restockForm.get('receipt_number')?.touched">
                  @if (restockForm.get('receipt_number')?.invalid && restockForm.get('receipt_number')?.touched) {
                    <div class="invalid-feedback d-block">
                      <i class="fa-solid fa-circle-exclamation me-1"></i>
                      Receipt number is required
                    </div>
                  }
                </div>
              </div>

              <!-- Cost Price & Selling Price -->
              <div class="col-md-6">
                <div class="form-group">
                  <label for="cost_price" class="form-label">
                    <i class="fa-solid fa-money-bill-wave me-1"></i>
                    Cost Price (&#8358;) *
                  </label>
                  <div class="input-group">
                    <span class="input-group-text" style="background: var(--bg-muted); border-color: var(--input-border); color: var(--text-secondary); font-weight: 600;">&#8358;</span>
                    <input type="number"
                           name="cost_price"
                           min="0.01"
                           step="0.01"
                           formControlName="cost_price"
                           class="form-control"
                           id="cost_price"
                           placeholder="0.00"
                           [class.is-invalid]="restockForm.get('cost_price')?.invalid && restockForm.get('cost_price')?.touched">
                  </div>
                  @if (restockForm.get('cost_price')?.invalid && restockForm.get('cost_price')?.touched) {
                    <div class="invalid-feedback d-block">
                      <i class="fa-solid fa-circle-exclamation me-1"></i>
                      Cost price must be greater than 0
                    </div>
                  }
                </div>
              </div>

              <div class="col-md-6">
                <div class="form-group">
                  <label for="selling_price" class="form-label">
                    <i class="fa-solid fa-tags me-1"></i>
                    Selling Price (&#8358;) *
                  </label>
                  <div class="input-group">
                    <span class="input-group-text" style="background: var(--bg-muted); border-color: var(--input-border); color: var(--text-secondary); font-weight: 600;">&#8358;</span>
                    <input type="number"
                           name="selling_price"
                           min="0.01"
                           step="0.01"
                           formControlName="selling_price"
                           class="form-control"
                           id="selling_price"
                           placeholder="0.00"
                           [class.is-invalid]="restockForm.get('selling_price')?.invalid && restockForm.get('selling_price')?.touched">
                  </div>
                  @if (restockForm.get('selling_price')?.invalid && restockForm.get('selling_price')?.touched) {
                    <div class="invalid-feedback d-block">
                      <i class="fa-solid fa-circle-exclamation me-1"></i>
                      Selling price must be greater than 0
                    </div>
                  }
                </div>
              </div>

              <!-- Mobile Receipt Upload -->
              <div class="col-12 d-block d-lg-none">
                <div style="padding-top: 16px; border-top: 1px solid var(--border);">
                  <div style="font-size: 13px; font-weight: 600; color: var(--text-primary); margin-bottom: 12px;">
                    <i class="fa-solid fa-receipt me-2" style="color: var(--accent);"></i>
                    Receipt Upload
                  </div>
                  @if (isUploadingReceipt) {
                    <div style="text-align: center; padding: 24px;">
                      <div class="spinner-border text-primary" role="status">
                        <span class="visually-hidden">Uploading...</span>
                      </div>
                      <div style="font-size: 13px; color: var(--text-tertiary); margin-top: 8px;">Uploading receipt...</div>
                      @if (uploadProgress2() > 0) {
                        <div style="margin-top: 12px; height: 6px; background: var(--bg-inset); border-radius: 3px; overflow: hidden;">
                          <div style="height: 100%; background: var(--accent); border-radius: 3px; transition: width 0.3s;"
                               [style.width.%]="uploadProgress2()"></div>
                        </div>
                        <div style="font-size: 11px; color: var(--text-tertiary); margin-top: 4px;">{{uploadProgress2()}}%</div>
                      }
                    </div>
                  } @else {
                    <div style="border: 2px dashed var(--border); border-radius: var(--radius-lg); padding: 20px; text-align: center; background: var(--bg-muted);"
                         [class.success]="uploadSuccess"
                         [class.error]="uploadError"
                         (drop)="onFileDrop2($event)"
                         (dragover)="onDragOver2($event)">
                      <div>
                        @if (showFile()) {
                          <i class="fa-solid fa-file-circle-check" style="font-size: 32px; color: var(--success-text); margin-bottom: 8px;"></i>
                        } @else {
                          <i class="fa-solid fa-cloud-arrow-up" style="font-size: 32px; color: var(--text-tertiary); margin-bottom: 8px;"></i>
                        }
                      </div>
                      <input type="file"
                             accept=".doc, .docx, .pdf, .txt, .png,.jpg,.jpeg"
                             (change)="onFileChange2($event)"
                             hidden
                             #fileInput2 />

                      @if (fileName()) {
                        <div style="font-weight: 600; font-size: 13px; color: var(--text-primary);">{{fileName()}}</div>
                        <div style="font-size: 12px; color: var(--text-tertiary); margin-top: 2px;">{{fileSize()}} KB</div>
                        <div style="display: flex; gap: 8px; justify-content: center; margin-top: 12px;">
                          <button type="button" class="btn btn-sm btn-outline-secondary" (click)="fileInput2.click()">
                            <i class="fa-solid fa-pen-to-square me-1"></i>
                            Change
                          </button>
                          <button type="button" class="btn btn-sm btn-outline-secondary" style="color: var(--danger-text); border-color: var(--danger-border);" (click)="removeFile()">
                            <i class="fa-solid fa-trash-can me-1"></i>
                            Remove
                          </button>
                        </div>
                      } @else {
                        <div style="font-size: 13px; color: var(--text-tertiary); margin-bottom: 10px;">Drag and drop your receipt file here</div>
                        <button type="button" class="btn btn-sm btn-outline-secondary" (click)="fileInput2.click()">
                          <i class="fa-solid fa-folder-open me-1"></i>
                          Select Receipt
                        </button>
                        <div style="font-size: 11px; color: var(--text-tertiary); margin-top: 8px;">PDF, DOC, DOCX, TXT, PNG, JPG, JPEG</div>
                      }
                    </div>
                  }
                </div>
              </div>

              <!-- Submit Button -->
              <div class="col-12" style="margin-top: 12px;">
                <button type="submit"
                        class="btn btn-primary"
                        style="width: 100%; padding: 12px 16px;"
                        [disabled]="restockForm.invalid || isLoading || isUploadingReceipt">
                  @if (isLoading) {
                    <span class="spinner-border spinner-border-sm me-2" role="status"></span>
                    Saving Stock...
                  } @else {
                    <i class="fa-solid fa-floppy-disk me-2"></i>
                    Save Stock
                  }
                </button>
              </div>
            </div>
          </form>
        }
      </div>
    </div>
  </div>

  <!-- Receipt Upload Card (Desktop) -->
  <div class="col-lg-6 d-none d-lg-block">
    <div class="card">
      <div style="padding: 16px 20px; border-bottom: 1px solid var(--border); background: var(--bg-muted); border-radius: var(--radius-lg) var(--radius-lg) 0 0;">
        <div style="display: flex; align-items: center; gap: 10px;">
          <div style="width: 30px; height: 30px; border-radius: var(--radius-sm); background: var(--info-bg); color: var(--info-text); display: flex; align-items: center; justify-content: center; font-size: 13px;">
            <i class="fa-solid fa-receipt"></i>
          </div>
          <div>
            <div style="font-size: 14px; font-weight: 600; color: var(--text-primary);">Receipt Upload</div>
            <div style="font-size: 12px; color: var(--text-tertiary);">Upload the purchase receipt for this restock</div>
          </div>
        </div>
      </div>
      <div style="padding: 24px;">
        @if (isUploadingReceipt) {
          <div style="text-align: center; padding: 40px 20px;">
            <div class="spinner-border text-primary" role="status" style="width: 3rem; height: 3rem;">
              <span class="visually-hidden">Uploading...</span>
            </div>
            <div style="font-size: 14px; color: var(--text-secondary); margin-top: 16px; font-weight: 500;">Uploading receipt...</div>
            @if (uploadProgress2() > 0) {
              <div style="margin-top: 20px; max-width: 300px; margin-left: auto; margin-right: auto;">
                <div style="height: 8px; background: var(--bg-inset); border-radius: 4px; overflow: hidden;">
                  <div style="height: 100%; background: var(--accent); border-radius: 4px; transition: width 0.3s;"
                       [style.width.%]="uploadProgress2()"></div>
                </div>
                <div style="font-size: 12px; color: var(--text-tertiary); margin-top: 6px;">{{uploadProgress2()}}%</div>
              </div>
            }
          </div>
        } @else {
          <div style="border: 2px dashed var(--border); border-radius: var(--radius-lg); padding: 40px; text-align: center; cursor: pointer; transition: all 0.2s; background: var(--bg-muted); min-height: 300px; display: flex; align-items: center; justify-content: center;"
               [class.success]="uploadSuccess"
               [class.error]="uploadError"
               (drop)="onFileDrop2($event)"
               (dragover)="onDragOver2($event)">
            <div>
              @if (showFile()) {
                <i class="fa-solid fa-file-circle-check" style="font-size: 48px; color: var(--success-text); margin-bottom: 16px;"></i>
              } @else {
                <i class="fa-solid fa-cloud-arrow-up" style="font-size: 48px; color: var(--text-tertiary); margin-bottom: 16px;"></i>
              }

              <input type="file"
                     accept=".doc, .docx, .pdf, .txt, .png,.jpg,.jpeg"
                     (change)="onFileChange2($event)"
                     hidden
                     #fileInput2 />

              @if (fileName()) {
                <div>
                  <div style="font-size: 16px; font-weight: 600; color: var(--text-primary); margin-bottom: 4px;">{{fileName()}}</div>
                  <div style="font-size: 13px; color: var(--text-tertiary); margin-bottom: 20px;">Size: {{fileSize()}} KB</div>
                  <div style="display: flex; gap: 12px; justify-content: center;">
                    <button type="button" class="btn btn-outline-secondary" (click)="fileInput2.click()">
                      <i class="fa-solid fa-pen-to-square me-2"></i>
                      Change File
                    </button>
                    <button type="button" class="btn btn-outline-secondary" style="color: var(--danger-text); border-color: var(--danger-border);" (click)="removeFile()">
                      <i class="fa-solid fa-trash-can me-2"></i>
                      Remove File
                    </button>
                  </div>
                </div>
              } @else {
                <div>
                  <div style="font-size: 16px; font-weight: 600; color: var(--text-primary); margin-bottom: 6px;">Upload Receipt</div>
                  <div style="font-size: 13px; color: var(--text-tertiary); margin-bottom: 20px;">
                    Drag and drop your receipt file here or click to browse
                  </div>
                  <button type="button" class="btn btn-primary" (click)="fileInput2.click()">
                    <i class="fa-solid fa-folder-open me-2"></i>
                    Select Receipt File
                  </button>
                  <div style="font-size: 11px; color: var(--text-tertiary); margin-top: 16px;">
                    <strong>Supported formats:</strong> PDF, DOC, DOCX, TXT, PNG, JPG, JPEG
                  </div>
                </div>
              }
            </div>
          </div>
        }
      </div>
    </div>
  </div>
</div>
